import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { getMe, logoutApi, setAuthToken, clearAuthToken } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // CO-7.12.b.2: bulk pre-fetch of project access entries on mount/login.
  // Subsequent permission checks happen synchronously against this Map via
  // useProjectPermissions(projectId). Single round-trip on login; zero in
  // steady state.
  const [projectAccessesMap, setProjectAccessesMap] = useState(new Map());
  const [accessesLoading, setAccessesLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // No localStorage check — rely on httpOnly cookie sent automatically.
    // SEC-1: /auth/me answers {user} only — the session lives in the httpOnly
    // cookie, never in the body. An older server that still sends
    // `access_token` is honoured, so this works against both.
    // On failure (401, network error, or missing data) we simply mark as not
    // authenticated and stop loading — no redirect, no retry loop.
    try {
      const response = await getMe();
      const { user: userData, access_token } = response.data || {};
      if (userData) {
        if (access_token) setAuthToken(access_token);
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    } catch {
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // CO-7.12.b.2: bulk-fetch UPAs whenever the user changes (initial mount or login).
  useEffect(() => {
    if (!user) {
      setProjectAccessesMap(new Map());
      setAccessesLoading(false);
      return;
    }
    let cancelled = false;
    setAccessesLoading(true);
    api.get('/users/me/project-accesses')
      .then((res) => {
        if (cancelled) return;
        const map = new Map();
        for (const entry of res.data?.accesses || []) {
          if (entry?.project_id && entry?.role) {
            map.set(entry.project_id, entry.role);
          }
        }
        setProjectAccessesMap(map);
        setAccessesLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[CO-7.12.b.2] Failed to fetch project accesses:', err);
        // Fail-closed: empty map. PO still has access via shortcut in useProjectPermissions.
        setProjectAccessesMap(new Map());
        setAccessesLoading(false);
      });
    return () => { cancelled = true; };
  }, [user]);

  const login = (token, userData) => {
    setAuthToken(token);
    setUser(userData);
    setIsAuthenticated(true);
  };

  // CO-4: refetch user from /auth/me. Used by CO-5 (User profile UI) when the
  // user updates their default_project_company_id and downstream components
  // need to see the new value without a full page reload.
  const refreshUser = async () => {
    try {
      const response = await getMe();
      const userData = response?.data?.user;
      if (userData) {
        setUser(userData);
        return userData;
      }
    } catch {
      // silent — caller decides whether to surface
    }
    return null;
  };

  const logout = async () => {
    try {
      await logoutApi(); // Clears the httpOnly cookie on the server
    } catch {
      // Best-effort — clear local state regardless
    }
    clearAuthToken();
    try { if (user?.id) window.localStorage.removeItem(`cqa.lastProject:${user.id}`); } catch { /* NAV-FE-1: storage blocked */ }
    setUser(null);
    setIsAuthenticated(false);
    setProjectAccessesMap(new Map());
  };

  // Tenant-wide flag preserved for navigation/admin route guards.
  // Project-scoped checks must use useProjectPermissions(projectId).
  const isPlatformOwner = () => user?.role === 'platform_owner';
  const isCompanyQAAdmin = () => user?.role === 'company_qa_admin';

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated,
      login,
      logout,
      isPlatformOwner,
      isCompanyQAAdmin,
      refreshUser,
      projectAccessesMap,
      accessesLoading,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
